<template>
  <div
    class="action-bar-cross-sell-card"
    :class="{
      'action-bar-cross-sell-card--highlighted': card.highlighted
    }"
    :data-testid="testId ?? `action-bar-cross-sell-card-${card.groupId}`"
  >
    <div class="action-bar-cross-sell-card__image">
      <Media
        v-if="productImage"
        :media="productImage"
        :cols="[3, 3, 6]"
        :lazyload="imageLazyload"
      />
    </div>
    <div class="action-bar-cross-sell-card__info">
      <div class="action-bar-cross-sell-card__name">
        {{ decodeHtmlEntities(card.displayName) }}
      </div>
      <div v-if="priceDeltaText" class="action-bar-cross-sell-card__price">
        {{ priceDeltaText }}
      </div>
    </div>
    <div v-if="hasLengthOptions" class="action-bar-cross-sell-card__length">
      <label class="visually-hidden" :for="lengthSelectId">
        {{ lengthSelectLabel }}
      </label>
      <PrimeVueSelect
        v-model="selectedSku"
        :input-id="lengthSelectId"
        class="action-bar-cross-sell-card__select"
        :options="lengthOptions"
        size="small"
        option-label="label"
        option-value="value"
        data-key="value"
        :disabled="disabled"
        :aria-label="lengthSelectLabel"
        :placeholder="lengthSelectPlaceholder"
        :append-to="overlayAppendTo"
        @update:model-value="onLengthChange"
      />
    </div>
    <div class="action-bar-cross-sell-card__action">
      <slot
        name="action"
        :sku="selectedSku"
        :group-id="card.groupId"
        :display-name="displayName"
        :disabled="disabled"
      >
        <Button
          type="button"
          style-type="attraction"
          size="small"
          icon-name="add-shopping-cart"
          icon-type="icon-only"
          :disabled="disabled"
          :aria-label="addToCartLabel"
          :title="addToCartLabel"
          :active-state="buttonState"
          :states="{
            loading: {
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.loading'] ??
                'pdp.addToCart.buttonState.loading',
              resetDelay: -1
            },
            success: {
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.success'] ??
                'pdp.addToCart.buttonState.success',
              resetDelay: -1
            },
            error: {
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.error'] ??
                'pdp.addToCart.buttonState.error',
              resetDelay: -1
            }
          }"
          @click="onAdd"
          @update:active-state="onButtonStateUpdate"
        >
          {{ addToCartLabel }}
        </Button>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, Media } from '@hypercodestudio/basler-components';
import type { BtnStateType } from '@hypercodestudio/basler-components';

import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';
import { crossSellCardPriceText } from '~/utils/shop/crossSellCardPriceText';
import { decodeHtmlEntities } from '~/utils/richtext/decodeHtmlEntities';
import { resolveProductImageUrlFromJson } from '~/utils/shop/resolveProductImageUrlFromJson';
import { generateImageObject } from '~/utils/generateImageObject';

const props = withDefaults(
  defineProps<{
    card: ActionBarCrossSellCardModel;
    disabled?: boolean;
    testId?: string;
    /** Images below the fold remain lazy by default. */
    imageLazyload?: boolean;
    /**
     * Target for the length overlay. Inside a modal the default body-appended
     * panel ends up behind the dialog, so pass `self` there.
     */
    overlayAppendTo?: HTMLElement | 'body' | 'self';
  }>(),
  {
    imageLazyload: true,
    overlayAppendTo: 'body',
    testId: undefined
  }
);

const emit = defineEmits<{
  add: [sku: string, groupId: string];
  'update:selectedSku': [sku: string, groupId: string];
}>();

const { $textDictionary, $activeLocale } = useNuxtApp();

const selectedSku = ref(props.card.selectedVariantSku);
const buttonState = ref<BtnStateType | undefined>('initial');

watch(
  () => props.card.selectedVariantSku,
  (sku) => {
    selectedSku.value = sku;
  }
);

const lengthSelectId = computed(
  () => `action-bar-cross-sell-length-${props.card.groupId}`
);

const displayName = computed(() => decodeHtmlEntities(props.card.displayName));

const lengthSelectPlaceholder = computed(
  () => $textDictionary.value['actionBar.crossSell.lengthLabel'] ?? 'Length'
);

const lengthSelectLabel = computed(
  () => `${lengthSelectPlaceholder.value}: ${displayName.value}`
);

const addToCartLabel = computed(() => {
  const label = $textDictionary.value['pdp.addToCart.label'] ?? 'Add to Cart';
  return `${label}: ${displayName.value}`;
});

const hasLengthOptions = computed(() => props.card.variants.length > 1);

const lengthOptions = computed(() =>
  props.card.variants.map((variant) => ({
    value: variant.sku,
    label: decodeHtmlEntities(
      variant.lengthLabel || variant.name || variant.sku
    )
  }))
);

const selectedVariant = computed(() =>
  props.card.variants.find((variant) => variant.sku === selectedSku.value)
);

const priceDeltaText = computed(() => {
  const variant = selectedVariant.value;
  return crossSellCardPriceText({
    priceValue: variant?.priceValue,
    currency: variant?.currency,
    locale: $activeLocale.value.webapp,
    fallbackText: props.card.priceDeltaText,
    onRequestLabel:
      $textDictionary.value['pdp.priceOnRequest.label'] ?? 'Price on request'
  });
});

const productImage = computed(() => {
  const raw = selectedVariant.value?.imageJson ?? props.card.imageJson;
  const imageUrl = raw
    ? resolveProductImageUrlFromJson(raw)
    : props.card.imageUrl;

  return generateImageObject(
    imageUrl
      ? {
          url: imageUrl,
          altTag: decodeHtmlEntities(props.card.displayName)
        }
      : undefined,
    {
      sizes: '200px',
      widths: [400, 600, 800, 1000],
      aspectRatio: [4, 3]
    }
  );
});

function onLengthChange(value: string): void {
  if (!value) {
    return;
  }
  emit('update:selectedSku', value, props.card.groupId);
}

function onButtonStateUpdate(state: BtnStateType): void {
  buttonState.value = state;
}

function onAdd(): void {
  buttonState.value = 'loading';
  emit('add', selectedSku.value, props.card.groupId);
}

watch(
  () => props.disabled,
  (disabled) => {
    if (!disabled && buttonState.value === 'loading') {
      buttonState.value = 'initial';
    }
  }
);
</script>

<style scoped>
.action-bar-cross-sell-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  min-width: 0;
}

/*
 * Products used in the BVS simulation. The inset shadow thickens the frame
 * without changing the box size, so highlighted and plain cards stay aligned.
 */
.action-bar-cross-sell-card--highlighted {
  border-color: #0094ff;
  box-shadow: inset 0 0 0 1px #0094ff;
}

.action-bar-cross-sell-card__image {
  flex: 0 0 64px;
  width: 64px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-bar-cross-sell-card__image :deep(.responsive-picture),
.action-bar-cross-sell-card__image :deep(img) {
  max-width: 100%;
  max-height: 100%;
}

.action-bar-cross-sell-card__image :deep(img) {
  object-fit: contain;
}

.action-bar-cross-sell-card__info {
  flex: 1 1 auto;
  min-width: 0;
}

.action-bar-cross-sell-card__name {
  font-size: 0.875rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.action-bar-cross-sell-card__price {
  font-size: 0.875rem;
  font-weight: 600;
  margin-block-start: 0.25rem;
}

.action-bar-cross-sell-card__length {
  flex: 0 0 auto;
}

.action-bar-cross-sell-card__select {
  min-width: 5.5rem;
  max-width: 7.5rem;
}

.action-bar-cross-sell-card__action {
  flex: 0 0 auto;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
